import { Injectable, inject } from '@angular/core';
import { InitialTicketingResponse } from '../../../dtos/response/initial-ticketing/initial-ticketing-response';
import { GenericResponseStatus } from '../../../models/generic-response-status';
import { MessageFactoryService } from '../../../services/message-service/message-factory.service';
import { MessageKey } from '../../../models/message/message-key';
import { VoluntaryReissueResponse } from '../../../dtos/response/voluntary-reissue/voluntary-reissue/voluntary-reissue-response';
import { initialTicketingSpecificErrors } from './ticketing-specific-errors';
import { MessageUtil } from '../message-util';

@Injectable({
  providedIn: 'root',
})
export class TicketingMessageConverter {
  private readonly messageFactoryService = inject(MessageFactoryService);
  private readonly messageUtil = inject(MessageUtil);
  private readonly preTicketingFailureRules: { pattern: RegExp; messageKey: MessageKey }[] = [
    {
      pattern: /payment was declined/i,
      messageKey: MessageKey.RESHOP_REISSUE_FAILURE_PAYMENT_DECLINED,
    },
    {
      pattern: /insufficient\s*funds/i,
      messageKey: MessageKey.RESHOP_REISSUE_FAILURE_INSUFFICIENT_FUNDS,
    },
    {
      pattern: /do\s*not\s*honor|donothonor/i,
      messageKey: MessageKey.RESHOP_REISSUE_FAILURE_DO_NOT_HONOR,
    },
  ];
  private readonly postTicketingFailureRules: { pattern: RegExp; messageKey: MessageKey }[] = [
    {
      pattern: /invalid ticket or certificate already created/i,
      messageKey: MessageKey.RESHOP_REISSUE_FAILURE_INVALID_CERTIFICATE,
    },
  ];

  /**
   * Converts initial ticketing messages.
   */
  convertInitialTicketingMessages(response: InitialTicketingResponse): InitialTicketingResponse {
    if (response.status === GenericResponseStatus.FAILURE) {
      response = this.mapInitialTicketingFullFailureErrors(response);
    }
    return response;
  }

  /**
   * Maps full failure ticketing errors to rich messages.
   */
  private mapInitialTicketingFullFailureErrors(response: InitialTicketingResponse): InitialTicketingResponse {
    const error = response?.error?.toLowerCase() ?? '';

    response.messages = this.messageUtil.mapPatternBasedErrorsToRichMessage(
      error,
      initialTicketingSpecificErrors,
      MessageKey.INITIAL_BOOKING_TICKETING_FAILURE
    );

    return response;
  }

  convertVoluntaryReissueMessages(response: VoluntaryReissueResponse): VoluntaryReissueResponse {
    const failure = response.error?.message;
    const postTicketingFailure = response.error?.failedBeforeOrOnReissueTickets === false;
    response.messages = [];
    if (response.status === GenericResponseStatus.FAILURE || postTicketingFailure) {
      const messageKey = postTicketingFailure
        ? this.resolveFailureMessageKey(failure, this.postTicketingFailureRules, MessageKey.RESHOP_REISSUE_FAILURE_POST_TICKETING)
        : this.resolveFailureMessageKey(failure, this.preTicketingFailureRules, MessageKey.RESHOP_REISSUE_FAILURE_PRE_TICKETING);
      const message = this.messageFactoryService.getRichMessage(messageKey);
      if (message) {
        response.messages.push(message);
      }
    }
    return response;
  }

  private resolveFailureMessageKey(
    failures: string | undefined,
    rules: { pattern: RegExp; messageKey: MessageKey }[],
    fallbackMessageKey: MessageKey
  ): MessageKey {
    if (!failures?.length) {
      return fallbackMessageKey;
    }

    const matchedRule = rules.find((rule) => rule.pattern.test(failures));
    return matchedRule?.messageKey ?? fallbackMessageKey;
  }
}
